/**
 * Lens dispensing illustrations.
 *
 * <lens-frame-art> draws a pair of glasses for a frame shape, in the frame's colour, with the
 * lenses filled by whatever tint / mirror has been chosen - so the tiles and the live preview are
 * all the same drawing. Only the left lens is described; the right is the same path mirrored.
 *
 * <lens-design-art> draws a single lens blank with its focal zones (single vision, progressive,
 * bifocal, office).
 */
const LENS_FRAME_SHAPES = {
	round: "M25,42 A30,30 0 1,0 85,42 A30,30 0 1,0 25,42 Z",
	oval: "M19,42 A36,25 0 1,0 91,42 A36,25 0 1,0 19,42 Z",
	rectangle: "M28,20 H82 Q90,20 90,28 V54 Q90,62 82,62 H28 Q20,62 20,54 V28 Q20,20 28,20 Z",
	square: "M32,14 H78 Q90,14 90,26 V58 Q90,70 78,70 H32 Q20,70 20,58 V26 Q20,14 32,14 Z",
	cateye: "M14,22 Q30,13 60,18 L86,22 Q92,24 90,36 Q86,64 56,64 Q28,64 22,46 Q17,34 14,22 Z",
	aviator: "M22,20 H86 Q93,20 90,32 Q86,58 64,66 Q50,70 38,64 Q20,54 20,32 Q20,20 22,20 Z",
	browline: "M22,24 H88 Q91,40 84,54 Q76,66 55,66 Q34,66 26,54 Q19,40 22,24 Z",
	rimless: "M26,22 H84 Q90,22 90,30 V50 Q90,62 78,62 H32 Q20,62 20,50 V30 Q20,22 26,22 Z",
};

app.component("lensFrameArt", {
	template: `
<svg class="lens-frame-art" viewBox="0 0 200 84" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
	<g ng-repeat="side in ::$ctrl.sides" ng-attr-transform="{{side}}">
		<path class="temple" d="M21,30 L3,25" ng-attr-stroke="{{$ctrl.colour || '#2b2d31'}}" ng-attr-stroke-width="{{$ctrl.stroke()}}" stroke-linecap="round" fill="none"/>
		<path class="lens-glass" ng-attr-d="{{$ctrl.path()}}" ng-attr-fill="{{$ctrl.glass()}}" ng-attr-fill-opacity="{{$ctrl.glassOpacity()}}"/>
		<path ng-if="$ctrl.tintHex && $ctrl.gradient" ng-attr-d="{{$ctrl.path()}}" fill="#ffffff" fill-opacity="0.55" transform="translate(0,26) scale(1,0.6)"/>
		<path ng-if="$ctrl.mirrorHex" ng-attr-d="{{$ctrl.path()}}" ng-attr-fill="{{$ctrl.mirrorHex}}" fill-opacity="0.55"/>
		<path class="rim" ng-attr-d="{{$ctrl.path()}}" fill="none"
			ng-attr-stroke="{{$ctrl.colour || '#2b2d31'}}" ng-attr-stroke-width="{{$ctrl.stroke()}}"
			ng-attr-stroke-dasharray="{{$ctrl.shape === 'rimless' ? '2 3' : ''}}" stroke-linejoin="round"/>
		<path ng-if="$ctrl.shape === 'browline'" d="M16,18 H93 Q96,31 89,31 H20 Q13,31 16,18 Z" ng-attr-fill="{{$ctrl.colour || '#2b2d31'}}"/>
		<path class="glare" d="M34,30 Q42,24 52,25" stroke="#ffffff" stroke-width="3" stroke-linecap="round" fill="none" stroke-opacity="0.8"/>
	</g>
	<path d="M89,36 Q100,26 111,36" fill="none" ng-attr-stroke="{{$ctrl.colour || '#2b2d31'}}" ng-attr-stroke-width="{{$ctrl.shape === 'rimless' ? 2.5 : $ctrl.stroke()}}" stroke-linecap="round"/>
	<path ng-if="$ctrl.shape === 'aviator'" d="M86,21 H114" fill="none" ng-attr-stroke="{{$ctrl.colour || '#2b2d31'}}" stroke-width="2.5"/>
</svg>`,
	bindings: {
		shape: "<",
		colour: "<",
		material: "<",
		tintHex: "<",
		tintOpacity: "<",
		gradient: "<",
		mirrorHex: "<",
	},
	controller: function () {
		const $ctrl = this;
		$ctrl.sides = ["", "translate(200,0) scale(-1,1)"];
		$ctrl.path = () => LENS_FRAME_SHAPES[$ctrl.shape] || LENS_FRAME_SHAPES.rectangle;
		$ctrl.stroke = () => {
			if ($ctrl.shape === "rimless") return 1.2;
			if ($ctrl.material === "Metal" || $ctrl.material === "Titanium") return 2.5;
			return 5.5;
		};
		$ctrl.glass = () => $ctrl.tintHex || "#dbe9ff";
		$ctrl.glassOpacity = () => ($ctrl.tintHex ? ($ctrl.tintOpacity || 50) / 100 : 0.45);
	},
});

// A pair of contact lenses, on the same 200 × 84 canvas as <lens-frame-art> so either fits the job
// thumbnails. Soft lenses are drawn full size, with toric orientation marks at six o'clock or
// multifocal zones; hard lenses (`family`) are smaller and tinted, ortho-k shows its reverse curve,
// and a scleral is wider than the cornea it vaults over.
app.component("lensContactArt", {
	template: `
<svg class="lens-contact-art" viewBox="0 0 200 84" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
	<g ng-repeat="cx in ::[52, 148]">
		<ellipse ng-attr-cx="{{cx}}" cy="74" ng-attr-rx="{{$ctrl.r() - 2}}" ry="5" fill="#0a2540" fill-opacity="0.06"/>
		<circle ng-if="$ctrl.family === 'scleral'" ng-attr-cx="{{cx}}" cy="40" r="23" fill="none" stroke="#a3aac0" stroke-width="1" stroke-dasharray="2 2"/>
		<circle ng-attr-cx="{{cx}}" cy="40" ng-attr-r="{{$ctrl.r()}}" ng-attr-fill="{{$ctrl.tint || '#cfe0ff'}}" ng-attr-fill-opacity="{{$ctrl.family === 'scleral' ? 0.3 : 0.55}}"
				ng-attr-stroke="{{$ctrl.family ? '#5f86c9' : '#8fb0ea'}}" ng-attr-stroke-width="{{$ctrl.family ? 2.4 : 2}}"/>
		<circle ng-attr-cx="{{cx}}" cy="40" ng-attr-r="{{$ctrl.r() - 7}}" fill="none" stroke="#8fb0ea" stroke-width="0.8" stroke-opacity="0.7"/>
		<g ng-if="$ctrl.design === 'orthok'">
			<circle ng-attr-cx="{{cx}}" cy="40" r="10" fill="#ffffff" fill-opacity="0.35"/>
			<circle ng-attr-cx="{{cx}}" cy="40" r="14" fill="none" stroke="#0035d2" stroke-width="2" stroke-opacity="0.45"/>
		</g>
		<g ng-if="$ctrl.design === 'multifocal'">
			<circle ng-attr-cx="{{cx}}" cy="40" ng-attr-r="{{$ctrl.family ? 7 : 9}}" fill="#20c997" fill-opacity="0.22" stroke="#20c997" stroke-width="0.8"/>
			<circle ng-attr-cx="{{cx}}" cy="40" ng-attr-r="{{$ctrl.family ? 12 : 17}}" fill="none" stroke="#00bcd4" stroke-width="0.8" stroke-dasharray="2 2"/>
		</g>
		<g ng-if="$ctrl.design === 'toric' && !$ctrl.family" stroke="#0035d2" stroke-width="1.6" stroke-linecap="round">
			<line ng-attr-x1="{{cx}}" y1="63" ng-attr-x2="{{cx}}" y2="69"/>
			<line ng-attr-x1="{{cx - 7}}" y1="62" ng-attr-x2="{{cx - 7}}" y2="67"/>
			<line ng-attr-x1="{{cx + 7}}" y1="62" ng-attr-x2="{{cx + 7}}" y2="67"/>
		</g>
		<path ng-attr-d="M{{cx - $ctrl.r() * 0.62}},{{40 - $ctrl.r() * 0.44}} Q{{cx - $ctrl.r() * 0.3}},{{40 - $ctrl.r() * 0.8}} {{cx + $ctrl.r() * 0.12}},{{40 - $ctrl.r() * 0.84}}"
				stroke="#ffffff" stroke-width="3.5" stroke-linecap="round" fill="none" stroke-opacity="0.9"/>
	</g>
	<text x="52" y="44" text-anchor="middle" class="lens-contact-eye">R</text>
	<text x="148" y="44" text-anchor="middle" class="lens-contact-eye">L</text>
</svg>`,
	bindings: {
		design: "<",
		family: "<",
		tint: "<",
	},
	controller: function () {
		const $ctrl = this;
		const RADII = { rgp: 22, orthok: 25, scleral: 36 };
		$ctrl.r = () => RADII[$ctrl.family] || 32;
	},
});

app.component("lensDesignArt", {
	template: `
<svg class="lens-design-art" viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
	<circle cx="50" cy="50" r="44" fill="#eef4ff" stroke="#b9cdf5" stroke-width="2"/>
	<g ng-switch="$ctrl.design">
		<g ng-switch-when="single">
			<circle cx="50" cy="50" r="34" fill="#0035d2" fill-opacity="0.16"/>
			<circle cx="50" cy="50" r="3" fill="#0035d2"/>
		</g>
		<g ng-switch-when="progressive">
			<path d="M14,40 Q50,20 86,40 Q80,22 50,8 Q20,22 14,40 Z" fill="#0035d2" fill-opacity="0.18"/>
			<path d="M14,40 Q50,20 86,40 L86,40 Q60,48 56,60 L44,60 Q40,48 14,40 Z" fill="#00bcd4" fill-opacity="0.18"/>
			<path d="M44,60 L56,60 Q66,70 70,86 Q50,96 30,86 Q34,70 44,60 Z" fill="#20c997" fill-opacity="0.3"/>
			<path d="M8,52 Q30,60 38,82 M92,52 Q70,60 62,82" stroke="#a3aac0" stroke-width="1.5" stroke-dasharray="2 3" fill="none"/>
		</g>
		<g ng-switch-when="bifocal">
			<circle cx="50" cy="50" r="40" fill="#0035d2" fill-opacity="0.14"/>
			<path d="M30,64 H70 Q70,82 50,82 Q30,82 30,64 Z" fill="#20c997" fill-opacity="0.35" stroke="#20c997" stroke-width="1.5"/>
		</g>
		<g ng-switch-when="office">
			<path d="M10,44 Q50,30 90,44 L90,52 Q50,40 10,52 Z" fill="#0035d2" fill-opacity="0.1"/>
			<path d="M10,52 Q50,40 90,52 L90,64 Q50,54 10,64 Z" fill="#00bcd4" fill-opacity="0.25"/>
			<path d="M10,64 Q50,54 90,64 Q86,84 50,92 Q14,84 10,64 Z" fill="#20c997" fill-opacity="0.3"/>
		</g>
	</g>
	<path d="M26,26 Q36,18 48,17" stroke="#ffffff" stroke-width="3" stroke-linecap="round" fill="none"/>
</svg>`,
	bindings: {
		design: "<",
	},
});
